/**
 * PRA new-architecture SPA shell.
 * AI agents: read ../../../AGENT_CONTEXT.md at session start; update it before handoff.
 * Coding rules: repo root AGENTS.md.
 */
import React, { Suspense, lazy, useState } from 'react';
import { BrowserRouter, NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { AppDataProvider } from './context/AppDataContext';
import { setCurrentUserId } from './index';
import { apiGet } from './api/client';
import { isAbortError } from './api/isAbortError';
import useAbortableEffect from './hooks/useAbortableEffect';
import { getCurrentUserEmail } from './auth/auth';
import { ENABLE_KLANTOMZET_PAGE } from './config/pageFeatureFlags';
import NewVersionNotice from './components/NewVersionNotice';
import AppNavUser from './components/AppNavUser';
import NoAccess from './pages/NoAccess/NoAccess';
import LogOutPage from './pages/LogOut/LogOutPage';

const FacturenVerwerken = lazy(() => import('./pages/FacturenVerwerken/FacturenVerwerken'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const ProjectPage = lazy(() => import('./pages/ProjectPage/ProjectPage'));
const MedewerkersPage = lazy(() => import('./pages/Medewerkers/MedewerkersPage'));
const LossProjectsPage = lazy(() => import('./pages/LossProjects/LossProjectsPage'));
const UserNormsPage = lazy(() => import('./pages/UserNorms/UserNormsPage'));
const ContractsPage = lazy(() => import('./pages/Contracts/ContractsPage'));
const TeamPlanningPage = lazy(() => import('./pages/TeamPlanning/TeamPlanningPage'));
const MijnPlanningPage = lazy(() => import('./pages/MijnPlanning/MijnPlanningPage'));
const ProjectPlanningPage = lazy(() => import('./pages/ProjectPlanning/ProjectPlanningPage'));
const RatesPage = lazy(() => import('./pages/Rates/RatesPage'));
const KlantomzetPage = ENABLE_KLANTOMZET_PAGE
    ? lazy(() => import('./pages/Klantomzet/KlantomzetPage'))
    : null;

const routeFallback = (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px' }}>
        Laden…
    </div>
);

function useResolveUser(shouldResolve) {
    const [ready, setReady] = useState(false);

    useAbortableEffect(async (signal, isActive) => {
        if (!shouldResolve) return;

        const email = getCurrentUserEmail();
        if (email) {
            try {
                const result = await apiGet(`getUserIdFromEmail?email=${encodeURIComponent(email)}`, undefined, {
                    signal,
                });
                if (!isActive()) return;
                if (Array.isArray(result) && result.length > 0) {
                    setCurrentUserId(result[0].user_id);
                }
            } catch (err) {
                if (!isAbortError(err)) {
                    console.error('Failed to resolve userId from email:', err);
                }
            }
        }
        if (isActive()) setReady(true);
    }, [shouldResolve]);

    return ready;
}

function AppShell() {

    return (
        <AppDataProvider>
            <BrowserRouter
                future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true,
                }}
            >
                <div className="app-container">
                    <NewVersionNotice />
                    <nav className="app-nav">
                        <div className="app-nav-left">
                            <span className="app-nav-title">PRA</span>
                            <div className="app-nav-links">
                                <NavLink
                                    to="/dashboard"
                                    className={({ isActive }) =>
                                        `app-nav-link${isActive ? ' active' : ''}`
                                    }
                                >
                                    Mijn PRA
                                </NavLink>
                                <NavLink
                                    to="/contracts"
                                    className={({ isActive }) =>
                                        `app-nav-link${isActive ? ' active' : ''}`
                                    }
                                >
                                    Klantovereenkomsten
                                </NavLink>
                                <NavLink
                                    to="/projecten"
                                    className={({ isActive }) =>
                                        `app-nav-link${isActive ? ' active' : ''}`
                                    }
                                >
                                    Projecten
                                </NavLink>
                                {ENABLE_KLANTOMZET_PAGE ? (
                                    <NavLink
                                        to="/klantomzet"
                                        className={({ isActive }) =>
                                            `app-nav-link${isActive ? ' active' : ''}`
                                        }
                                    >
                                        Klantomzet
                                    </NavLink>
                                ) : null}
                                <NavLink
                                    to="/lossProjects"
                                    className={({ isActive }) =>
                                        `app-nav-link${isActive ? ' active' : ''}`
                                    }
                                >
                                    Verliesrapportage
                                </NavLink>
                                <NavLink
                                    to="/facturatie"
                                    className={({ isActive }) =>
                                        `app-nav-link${isActive ? ' active' : ''}`
                                    }
                                >
                                    Facturatie
                                </NavLink>
                                <NavLink
                                    to="/medewerkers"
                                    className={({ isActive }) =>
                                        `app-nav-link${isActive ? ' active' : ''}`
                                    }
                                >
                                    Medewerkers
                                </NavLink>
                                <NavLink
                                    to="/mijn_planning"
                                    className={({ isActive }) =>
                                        `app-nav-link${isActive ? ' active' : ''}`
                                    }
                                >
                                    Mijn Planning
                                </NavLink>
                                <NavLink
                                    to="/project_planning"
                                    className={({ isActive }) =>
                                        `app-nav-link${isActive ? ' active' : ''}`
                                    }
                                >
                                    Project Planning
                                </NavLink>
                                <NavLink
                                    to="/team_planning"
                                    className={({ isActive }) =>
                                        `app-nav-link${isActive ? ' active' : ''}`
                                    }
                                >
                                    Team Planning
                                </NavLink>
                                <NavLink
                                    to="/userNorms"
                                    className={({ isActive }) =>
                                        `app-nav-link${isActive ? ' active' : ''}`
                                    }
                                >
                                    Normen aanpassen
                                </NavLink>
                                <NavLink
                                    to="/uurtarieven"
                                    className={({ isActive }) =>
                                        `app-nav-link${isActive ? ' active' : ''}`
                                    }
                                >
                                    Uurtarieven en normpercentages
                                </NavLink>
                            </div>
                        </div>
                        <div className="app-nav-right">
                            <AppNavUser />
                            <NavLink
                                to="/logout"
                                className={({ isActive }) =>
                                    `app-nav-link app-nav-link-logout${isActive ? ' active' : ''}`
                                }
                            >
                                Log uit
                            </NavLink>
                        </div>
                    </nav>
                    <main className="app-main">
                    <Suspense fallback={routeFallback}>
                    <Routes>
                        <Route path="/facturatie" element={<FacturenVerwerken />} />
                        <Route path="/facturen-verwerken" element={<Navigate to="/facturatie" replace />} />
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/projecten" element={<ProjectPage />} />
                        {ENABLE_KLANTOMZET_PAGE && KlantomzetPage ? (
                            <Route path="/klantomzet" element={<KlantomzetPage />} />
                        ) : null}
                        <Route path="/medewerkers" element={<MedewerkersPage />} />
                        <Route path="/team_planning" element={<TeamPlanningPage />} />
                        <Route path="/mijn_planning" element={<MijnPlanningPage />} />
                        <Route path="/project_planning" element={<ProjectPlanningPage />} />
                        <Route path="/lossProjects" element={<LossProjectsPage />} />
                        <Route path="/userNorms" element={<UserNormsPage />} />
                        <Route path="/contracts" element={<ContractsPage />} />
                        <Route path="/uurtarieven" element={<RatesPage />} />
                        <Route path="/no-access" element={<NoAccess />} />
                        <Route path="/logout" element={<LogOutPage />} />
                        <Route
                            path="/authentication-response"
                            element={<Navigate to="/dashboard" replace />}
                        />
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                    </Suspense>
                    </main>
                </div>
            </BrowserRouter>
        </AppDataProvider>
    );
}

function MsalAppContent() {
    const { accounts } = useMsal();
    const ready = useResolveUser(accounts.length > 0);

    if (!ready) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <div>Gebruiker verifiëren...</div>
            </div>
        );
    }

    return <AppShell />;
}

function FakeUserAppContent() {
    const ready = useResolveUser(true);

    if (!ready) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <div>Gebruiker verifiëren (fakeUserAs)...</div>
            </div>
        );
    }

    return <AppShell />;
}

export default function App({ fakeUser }) {
    if (fakeUser) return <FakeUserAppContent />;
    return <MsalAppContent />;
}
